import {DialogConfig} from "./types";

/** What a locked flow decides to do with a candidate dialog. */
export type FlowDecision = "allow" | "queue" | "block";

/**
 * A flow lock describes how the UI should behave while a critical user journey
 * is in progress (deposit, KYC, auth…). Example: during `deposit`, marketing is
 * blocked, bonus dialogs are queued, only system/critical may interrupt.
 */
export interface FlowLock {
    name: string;
    /** Decide per candidate dialog. Defaults applied if omitted. */
    decide?: (config: DialogConfig) => FlowDecision;
    /** Convenience rules used by the default `decide`. */
    block?: DialogConfig["category"][];
    queue?: DialogConfig["category"][];
    allow?: DialogConfig["category"][];
    lockedAt: number;
}

export type FlowLockInput = Omit<FlowLock, "lockedAt" | "decide"> &
    Partial<Pick<FlowLock, "decide">>;

/**
 * Tracks active flow locks and resolves the *strictest* decision across all of
 * them for a given dialog. Multiple flows can be locked simultaneously
 * (e.g. `deposit` + `auth`); `block` beats `queue` beats `allow`.
 */
export class FlowManager {
    private readonly locks = new Map<string, FlowLock>();

    lock(input: FlowLockInput): void {
        this.locks.set(input.name, {...input, lockedAt: Date.now()});
    }

    unlock(name: string): void {
        this.locks.delete(name);
    }

    isLocked(name?: string): boolean {
        return name ? this.locks.has(name) : this.locks.size > 0;
    }

    active(): string[] {
        return [...this.locks.keys()];
    }

    /** Strictest decision wins across every active lock. */
    decide(config: DialogConfig): FlowDecision {
        let decision: FlowDecision = "allow";
        for (const lock of this.locks.values()) {
            const d = this.decideForLock(lock, config);
            decision = strictest(decision, d);
            if (decision === "block") break;
        }
        return decision;
    }

    private decideForLock(lock: FlowLock, config: DialogConfig): FlowDecision {
        if (lock.decide) return lock.decide(config);

        const category = config.category ?? "utility";
        if (lock.allow?.includes(category)) return "allow";
        if (lock.block?.includes(category)) return "block";
        if (lock.queue?.includes(category)) return "queue";

        // Sensible default: critical/system always allowed, everything else queued.
        if (category === "system") return "allow";
        return "queue";
    }
}

const RANK: Record<FlowDecision, number> = {allow: 0, queue: 1, block: 2};

function strictest(a: FlowDecision, b: FlowDecision): FlowDecision {
    return RANK[a] >= RANK[b] ? a : b;
}
